import { getWebPath } from '@doss/common-all'
import { MoreHoriz } from '@mui/icons-material'
import { useEffect, useMemo, useState } from 'react'
import { FiArrowLeft, FiPlus, FiSidebar } from 'react-icons/fi'
import { PiDatabase, PiGraph } from 'react-icons/pi'
import { RxCross2, RxDashboard, RxInput, RxLightningBolt, RxTable } from 'react-icons/rx'
import { Link, useNavigate, useParams } from 'react-router'
import { MenuDropdown } from '~/@/components/inputs/selection/menu-dropdown'
import { cn } from '~/@/lib/utils'
import config from '~/common/config/config'
import { BreadcrumbObjectType } from '~/common/generated/apollo-operations'
import { useBreadcrumbs } from '~/common/hooks/useBreadcrumbs'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { useGlobalContext } from '~/common/hooks/useGlobalContext'
import { useOrgProjects } from '~/common/hooks/useOrgProjects'
import { usePage } from '~/common/hooks/usePage'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { getAdminUrl } from '~/common/utils/navigation'
import { AnimatedDossbotIcon } from '~/components/dossbot/AnimatedDossbotIcon'
import { ConfigbaseNav } from '~/components/left-nav/configbase-nav/ConfigbaseNav'
import { useConfigbaseNavEnabled } from '~/components/left-nav/configbase-nav/useConfigbaseNavEnabled'
import { SearchInput } from '../navigation/SearchInput'
import { SendEmailDialog } from '../shared/SendEmailDialog'
import { LeftNavFooter } from './LeftNavFooter'
import { LeftNavNotificationButton } from './LeftNavNotificationButton'
import { ProjectsDropdown } from './ProjectsDropdown'
import { ProjectWorkspaceNav } from './ProjectWorkspaceNav'

type LeftNavProps = {
  expanded: boolean
  isPeeking?: boolean
  isMobile?: boolean
  onClose?: () => void
}

export const LeftNav = ({
  expanded,
  isPeeking = false,
  isMobile = false,
  onClose,
}: LeftNavProps) => {
  const { pageType, objectType, objectId, group, pathname } = usePage()
  const isFormsActive = group === 'forms'
  const isWorkflowsActive = group === 'workflows'
  const isDossbotActive = group === 'dossbot'
  const isDependencyExplorerActive = group === 'dependencyExplorer'
  const isDataSourcesActive = pathname === '/settings/data-sources'
  const isTablePage = pageType === 'table'
  const { isInternal, isAdmin, user } = useGlobalContext()
  const { isEnabled: isConfigbaseNavEnabled } = useConfigbaseNavEnabled()
  const { dispatch } = useDossSignals()
  const { breadcrumbs } = useBreadcrumbs()
  const { selectedProject, setSelectedProject, projects, workspace } = useOrgProjects()
  const { t } = useTypedTranslation()
  const navigate = useNavigate()
  const params = useParams()
  const tableId = isTablePage ? params.table_id : undefined

  // Check if the current table (or any ancestor table in nested workspaces) has a view in the
  // selected project's workspace. This handles tables whose views exist in workspaces that are
  // nested under other table workspaces within the project.
  //
  // Example: Table C has a view in Table B's workspace, which itself is in Table A's workspace,
  // which is in Project X. When viewing Table C with Project X selected, we check if Table A,
  // Table B, or Table C has a view in Project X's workspace.
  const tableHasViewInSelectedProject = useMemo(() => {
    if (!selectedProject || !tableId || !workspace?.views) {
      return false
    }

    // Get all table breadcrumbs in the navigation path
    const tableBreadcrumbs = breadcrumbs.filter(
      (b) => b.workspaceType === BreadcrumbObjectType.TABLE,
    )
    const currentTableIndex = tableBreadcrumbs.findIndex((b) => b.id === tableId)

    // Extract IDs of all tables that appear before the current table in the breadcrumb path.
    // These represent tables whose workspaces are ancestors of the current table's workspace.
    const ancestorTableIds =
      currentTableIndex > 0
        ? tableBreadcrumbs
            .slice(0, currentTableIndex)
            .map((b) => b.id)
            .filter(Boolean)
        : []

    // Check if the current table OR any ancestor table has a view in the selected project's workspace
    return workspace.views.some(
      (view) => view.table?.id === tableId || ancestorTableIds.includes(view.table?.id),
    )
  }, [selectedProject, tableId, workspace?.views, breadcrumbs])

  const [openEmailDialog, setOpenEmailDialog] = useState(false)

  const addNewMenuItems = useMemo(() => {
    if (!workspace?.id) {
      return []
    }
    return [
      {
        label: t('add_new_button.table'),
        icon: <RxTable size={16} />,
        onClick: () => {
          navigate(
            getWebPath({
              objectType: BreadcrumbObjectType.TABLE,
              workspaceId: workspace.id,
            }),
          )
        },
      },
      {
        label: t('add_new_button.dashboard'),
        icon: <RxDashboard size={16} />,
        onClick: () => {
          navigate(
            getWebPath({
              objectType: BreadcrumbObjectType.WORKSPACE,
              objectId: workspace.id,
              creating: true,
            }),
          )
        },
      },
      {
        label: t('add_new_button.forms'),
        icon: <RxInput size={16} />,
        onClick: () => {
          navigate(
            getWebPath({
              objectType: BreadcrumbObjectType.FORM,
              workspaceId: workspace.id,
            }),
          )
        },
      },
      {
        label: t('add_new_button.more'),
        icon: <MoreHoriz style={{ fontSize: 16 }} />,
        onClick: () => {
          setOpenEmailDialog(true)
        },
      },
    ]
  }, [workspace?.id, t, navigate])

  // Sync selected project to match the page we're viewing. On table pages: only sync if the
  // current page doesn't belong to the selected project (so we don't switch away when the user
  // is viewing a table in their chosen project; we do switch when they open a table that only
  // exists in another project).
  useEffect(() => {
    const projectInBreadcrumb = breadcrumbs.find(
      (breadcrumb) => breadcrumb.workspaceType === BreadcrumbObjectType.PROJECT,
    )
    if (!projectInBreadcrumb || selectedProject?.id === projectInBreadcrumb.id) {
      return
    }
    // On table pages, if the table has a view in the selected project's workspace, don't switch.
    // Wait for workspace data to load before making a decision.
    if (isTablePage && tableId) {
      if (!workspace) {
        // Still loading workspace data, don't make a decision yet
        return
      }
      if (tableHasViewInSelectedProject) {
        // Table has a view in the selected project, keep current selection
        return
      }
    }
    const matchingProject = projects.find((project) => project.id === projectInBreadcrumb.id)
    // Only update selectedProject if we found a valid matching project
    if (matchingProject) {
      setSelectedProject(matchingProject)
    }
  }, [
    breadcrumbs,
    isTablePage,
    projects,
    selectedProject?.id,
    setSelectedProject,
    workspace,
    tableHasViewInSelectedProject,
    tableId,
  ])

  return (
    <div
      className={cn(
        'bg-gray-1 border-gray-4 group flex size-full flex-col overflow-hidden border-e transition-[width] duration-200',
        isPeeking && 'border-y rounded-md',
      )}
    >
      {/* Header: Project Switcher + Collapse */}
      <div className="relative flex shrink-0 items-center justify-between py-[6px] pe-2 ps-2 text-doss-active">
        <ProjectsDropdown
          project={selectedProject}
          projects={projects}
          setProject={setSelectedProject}
        />
        <div className={cn('flex items-center gap-1', !expanded && 'hidden')}>
          {!isMobile && workspace?.id && (isAdmin || isInternal) && (
            <MenuDropdown
              items={addNewMenuItems}
              menuClassName="w-48"
              side
              sideOffset={8}
              trigger={
                <div className="flex size-6 cursor-pointer items-center justify-center rounded text-gray-11 transition duration-200 hover:bg-gray-2 hover:text-gray-12">
                  <FiPlus size={16} />
                </div>
              }
            />
          )}
          {!isMobile && !isPeeking && (
            <div
              className="group/expand relative flex size-6 cursor-pointer items-center justify-center rounded text-gray-11 transition duration-200 hover:bg-gray-2 hover:text-gray-12"
              onClick={() => dispatch(DossSignalType.COLLAPSE_LEFT_NAV_CLICKED)}
            >
              <FiSidebar size={16} />
              <FiArrowLeft className="absolute end-1 hidden group-hover/expand:block" size={10} />
            </div>
          )}
          {onClose && (
            <button
              className="flex size-8 shrink-0 items-center justify-center rounded-full text-gray-10 hover:bg-gray-3"
              onClick={onClose}
            >
              <RxCross2 size={18} />
            </button>
          )}
        </div>
      </div>

      {/* Search Input */}
      {user && (
        <div className="shrink-0 px-2">
          <SearchInput isMobile={isMobile} shouldShowGlobalLoading={false} />
        </div>
      )}

      {/* Notifications */}
      {user && (
        <div className="shrink-0 px-2">
          <LeftNavNotificationButton isMobile={isMobile} />
        </div>
      )}
      {!isMobile && (
        <div className="shrink-0 px-2 pb-2.5">
          <Link
            className={cn(
              'flex items-center gap-2 px-3 py-2 rounded cursor-pointer whitespace-nowrap',
              isDossbotActive ? 'bg-gray-3' : 'hover:bg-gray-2',
            )}
            to="/dossbot/chat"
          >
            <div className="flex size-5 shrink-0 items-center justify-center">
              <AnimatedDossbotIcon
                animate={false}
                className={cn(isDossbotActive ? 'text-blue-8' : 'text-gray-10')}
                height={18}
                width={18}
              />
            </div>
            <span
              className={cn('text-sm truncate', isDossbotActive ? 'text-gray-12' : 'text-gray-10')}
            >
              {t('left_nav.dossbot')}
            </span>
          </Link>
        </div>
      )}

      {/* Divider */}
      <div className="mb-3 shrink-0 border-t border-gray-4" />

      {/* Main Content Area */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Scrollable workspace navigation */}
        <div className="min-h-0 flex-1 overflow-y-auto whitespace-nowrap">
          {isConfigbaseNavEnabled ? (
            <ConfigbaseNav />
          ) : (
            selectedProject && (
              <ProjectWorkspaceNav breadcrumbs={breadcrumbs} project={selectedProject} />
            )
          )}
        </div>

        {!isMobile && (
          <div className="shrink-0 border-t border-gray-4 p-2">
            <Link
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded cursor-pointer',
                isDataSourcesActive ? 'bg-gray-3' : 'hover:bg-gray-2',
              )}
              to="/settings/data-sources"
            >
              <div className="flex size-5 shrink-0 items-center justify-center">
                <PiDatabase
                  className={cn(isDataSourcesActive ? 'text-blue-8' : 'text-gray-10')}
                  size={18}
                />
              </div>
              <span
                className={cn(
                  'min-w-0 text-sm truncate',
                  isDataSourcesActive ? 'text-gray-12' : 'text-gray-10',
                )}
              >
                {t('left_nav.data_sources')}
              </span>
            </Link>
            {(isInternal || isAdmin) && (
              <Link
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded cursor-pointer',
                  isWorkflowsActive ? 'bg-gray-3' : 'hover:bg-gray-2',
                )}
                to={getWebPath({ objectType: BreadcrumbObjectType.WORKFLOW, workflow: {} })}
              >
                <div className="flex size-5 shrink-0 items-center justify-center">
                  <RxLightningBolt
                    className={cn(isWorkflowsActive ? 'text-yellow-9' : 'text-gray-10')}
                    size={18}
                  />
                </div>
                <span
                  className={cn(
                    'min-w-0 text-sm truncate',
                    isWorkflowsActive ? 'text-gray-12' : 'text-gray-10',
                  )}
                >
                  {t('left_nav.workflows')}
                </span>
              </Link>
            )}
            <Link
              className={cn(
                'flex items-center gap-2 px-3 py-2 rounded cursor-pointer',
                isFormsActive ? 'bg-gray-3' : 'hover:bg-gray-2',
              )}
              to={getWebPath({ objectType: BreadcrumbObjectType.FORM, home: true })}
            >
              <div className="flex size-5 shrink-0 items-center justify-center">
                <RxInput
                  className={cn(isFormsActive ? 'text-purple-8' : 'text-gray-10')}
                  size={18}
                />
              </div>
              <span
                className={cn(
                  'min-w-0 text-sm truncate',
                  isFormsActive ? 'text-gray-12' : 'text-gray-10',
                )}
              >
                {t('left_nav.forms')}
              </span>
            </Link>
            {isInternal && (
              <Link
                className={cn(
                  'flex items-center gap-2 px-3 py-2 rounded cursor-pointer whitespace-nowrap',
                  isDependencyExplorerActive ? 'bg-gray-3' : 'hover:bg-gray-2',
                )}
                to="/dependency-explorer"
              >
                <div className="flex size-5 shrink-0 items-center justify-center">
                  <PiGraph
                    className={cn(isDependencyExplorerActive ? 'text-blue-8' : 'text-gray-10')}
                    size={18}
                  />
                </div>
                <span
                  className={cn(
                    'min-w-0 text-sm truncate',
                    isDependencyExplorerActive ? 'text-gray-12' : 'text-gray-10',
                  )}
                >
                  {t('left_nav.dependency_explorer')}
                </span>
              </Link>
            )}
          </div>
        )}
      </div>

      {/* Footer: Account Settings, Invite Users, Version & Help */}
      {!isMobile && user && <LeftNavFooter isPeeking={isPeeking} />}

      {/* Send Email Dialog for "More" menu item */}
      {!isMobile && (
        <SendEmailDialog
          defaultBody={t('add_new_button.email_body')}
          defaultSubject={`[${user?.organizationName}] ${t('add_new_button.request_to_add_workspace_block')}`}
          defaultTo={config.supportEmail}
          emailBodyAdmin={t('add_new_button.email_body_admin', {
            name: `${user?.firstName} ${user?.lastName}`,
            email: user?.email,
            organizationId: user?.organizationId,
            url: typeof window !== 'undefined' ? window.location.href : '',
            adminUrl: getAdminUrl(
              objectType || BreadcrumbObjectType.PROJECT,
              objectId || '',
              user?.organizationId || '',
            ),
          })}
          onClose={() => setOpenEmailDialog(false)}
          open={openEmailDialog}
          title={t('send_email_dialog.title')}
        />
      )}
    </div>
  )
}
